import { withErrorContext } from ".";
import { Editor, Element, Location, Path, Range, Text, Transforms } from "slate";
import { ReactEditor } from "slate-react";

import { trackEngagement } from "helpers/analytics";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ExternalImageElement, LocalImageElement } from "../types";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import EditorImage from "./images/EditorImage";

export enum EditorImageType {
  LocalImage = "local",
  ExternalImage = "external",
}

export type EditorImageProps = ILocalEditorImageProps | IExternalEditorImageProps;
export interface IExternalEditorImageProps {
  imageType: EditorImageType.ExternalImage;
  url: string;
  alt?: string;
  width?: string;
  height?: string;
}

export interface ILocalEditorImageProps {
  imageType: EditorImageType.LocalImage;
  data?: string;
  resourceID?: string;
  localID?: string;
  fileName?: string;
  url?: string;
  width?: string;
  height?: string;
  alt?: string;
}

export function isLocalImageElement(element: Element): element is LocalImageElement {
  return element.type === "image" && element.imageType === EditorImageType.LocalImage;
}

export function isExternalImageElement(element: Element): element is ExternalImageElement {
  return element.type === "image" && element.imageType === EditorImageType.ExternalImage && !!element.url;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const notInsideVoidParent = (editor: Editor, selection: Location) => !Editor.void(editor, { at: selection });

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getTargetSelection = (editor: Editor, selection: Location) => {
  const [node] = Editor.node(editor, selection);

  let path = null;
  if (
    (Text.isText(node) && notInsideVoidParent(editor, selection)) ||
    (Element.isElement(node) && !editor.isVoid(node))
  ) {
    path = selection;
  } else {
    // @ts-expect-error TS(2488)
    const [, foundPath] = Editor.above(editor, {
      at: selection,
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (n: any) => Element.isElement(n) && !editor.isInline(n) && !editor.isVoid(n),
      mode: "lowest",
    });

    path = Editor.end(editor, foundPath);
  }

  return path;
};

/* eslint-disable no-param-reassign */
export function withImages(editor: Editor): Editor {
  const { isInline, isVoid, insertData } = editor as ReactEditor;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  editor.isVoid = (element: any) => (element.type === "image" ? true : isVoid(element));
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  editor.isInline = (element: any) => (element.type === "image" ? true : isInline(element));

  editor.insertExternalImage = (
    url: string,
    options: { width?: string; height?: string; savedSelection?: Location }
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  ) => {
    const { width, height, savedSelection } = options;

    const dims = width && height;
    const targetSelection = getTargetSelection(editor, savedSelection || editor.selection || Editor.start(editor, []));

    Transforms.insertNodes(
      editor,
      {
        type: "image",
        imageType: EditorImageType.ExternalImage,
        url,
        children: [{ text: "" }],
        ...(dims ? { width, height } : {}),
      },
      { voids: true, at: targetSelection, select: true }
    );

    Editor.normalize(editor, { force: true });
    if (!ReactEditor.isFocused(editor as ReactEditor)) {
      ReactEditor.focus(editor as ReactEditor);
    }

    Transforms.select(
      editor,
      // @ts-expect-error TS(2339)
      Path.next(Path.next(Range.isRange(targetSelection) ? Range.start(targetSelection).path : targetSelection.path))
    );

    trackEngagement("Editor: Inline image inserted", editor.eventPlace, { type: "external" });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  editor.insertData = (data: any) => {
    if (!data) {
      return;
    }

    const { files } = data;

    if (files && files.length > 0) {
      // @ts-expect-error TS(2345): Argument of type '(file: File) => void' is not ass... Remove this comment to see the full error message
      Array.from(files).forEach((file: File) => {
        const [mime] = file.type.split("/");

        if (mime === "image") {
          const blobUrl = URL.createObjectURL(file);
          editor.insertLocalImage(blobUrl, file.name, {});
        }
      });
    } else {
      insertData(data);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  editor.insertLocalImage = (data: string, fileName: string, options: { width?: string; height?: string }) => {
    const { width, height } = options;
    const dims = width && height;

    const targetSelection = getTargetSelection(editor, editor.selection || Editor.start(editor, []));

    Transforms.insertNodes(
      editor,
      {
        type: "image",
        imageType: EditorImageType.LocalImage,
        fileName,
        data,
        children: [{ text: "" }],
        ...(dims ? { width, height } : {}),
      },
      { voids: true, at: targetSelection }
    );

    Editor.normalize(editor, { force: true });
    if (!ReactEditor.isFocused(editor as ReactEditor)) {
      ReactEditor.focus(editor as ReactEditor);
    }
    Transforms.select(
      editor,
      // @ts-expect-error TS(2339)
      Path.next(Path.next(Range.isRange(targetSelection) ? Range.start(targetSelection).path : targetSelection.path))
    );

    trackEngagement("Editor: Inline image inserted", editor.eventPlace, { type: "local" });
  };

  return editor;
}
/* eslint-enable no-param-reassign */

export const ReactPart: import("./index").ReactPart = {
  renderElement(props, _editor, next) {
    switch (props.element.type) {
      case "image":
        if (isLocalImageElement(props.element)) {
          return (
            <span {...props.attributes} data-testid={`editor-local-image-${props.element.fileName}`}>
              <EditorImage element={props.element} />
              {props.children}
            </span>
          );
        }
        if (isExternalImageElement(props.element)) {
          return (
            <span
              data-testid={`editor-external-image-${props.element.url}`}
              {...props.attributes}
              style={{ display: "inline-block", fontSize: "0px", padding: "0 4px", verticalAlign: "text-bottom" }}
            >
              <EditorImage element={props.element} />
              {props.children}
            </span>
          );
        }
        return next(props);

      default:
        return next(props);
    }
  },
};

const pluginSpec: import("./index").PluginSpec = [withErrorContext(withImages), ReactPart];

export default pluginSpec;
